import React from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogContent,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    IconHome,
    IconChecks,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';

// ─── Constants ───────────────────────────────────────────────────────────────
const PRIMARY = colors.carbon_bg_main || '#00B4D8';
const PRIMARY_DARK = '#009DC0';

const congratsTranslations = {
    en: {
        title: 'Congratulations!',
        subtitle: 'Your carbon project details have been successfully submitted.',
        goDashboard: 'Go to Dashboard',
        viewProject: 'View Project Status',
    },
    hi: {
        title: 'बधाई हो!',
        subtitle: 'आपके कार्बन प्रोजेक्ट का विवरण सफलतापूर्वक जमा कर दिया गया है।',
        goDashboard: 'डैशबोर्ड पर जाएं',
        viewProject: 'प्रोजेक्ट की स्थिति देखें',
    },
    mr: {
        title: 'अभिनंदन!',
        subtitle: 'तुमच्या कार्बन प्रकल्पाचे तपशील यशस्वीरित्या सबमिट केले गेले आहेत.',
        goDashboard: 'डॅशबोर्डवर जा',
        viewProject: 'प्रकल्पाची स्थिती पहा',
    },
};

const CarbonCreditsCongratulations = ({ open, onClose, isProof = false }) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const langCode = i18n.language || 'en';
    const currentLang = langCode.startsWith('hi') ? 'hi' : (langCode.startsWith('mr') || langCode.startsWith('mar')) ? 'mr' : 'en';
    const tLocal = congratsTranslations[currentLang];

    if (isProof) {
        tLocal.subtitle = currentLang === 'en' ? 'Your proof has been successfully uploaded for verification.' : 
                        currentLang === 'hi' ? 'आपका प्रमाण सत्यापन के लिए सफलतापूर्वक अपलोड कर दिया गया है।' : 
                        'तुमचा पुरावा पडताळणीसाठी यशस्वीरित्या अपलोड केला गेला आहे.';
    }

    const handleGoDashboard = () => {
        onClose();
        navigate('/carbon-credits');
    };

    const handleViewProject = () => {
        onClose();
        navigate('/carbon-credits/all-projects');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    padding: { xs: 2, md: 4 },
                    maxWidth: '440px',
                    width: '100%',
                    textAlign: 'center',
                }
            }}
        >
            <DialogContent>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                }}>
                    {/* Celebration Icon/Illustration Placeholder */}
                    <Box sx={{
                        width: 120,
                        height: 120,
                        backgroundColor: colors.carbon_bg_selected || 'rgba(0, 180, 216, 0.1)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '60px',
                    }}>
                        🌱
                    </Box>

                    <Box>
                        <Typography sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 800,
                            fontSize: '28px',
                            color: colors.carbon_text_main || '#1C252E',
                            mb: 1,
                        }}>
                            {tLocal.title}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: '16px',
                            color: colors.carbon_text_secondary || '#637381',
                            lineHeight: 1.5
                        }}>
                            {tLocal.subtitle}
                        </Typography>
                    </Box>

                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                    }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleGoDashboard}
                            startIcon={<IconHome size={20} />}
                            sx={{
                                borderRadius: '50px',
                                height: '52px',
                                backgroundColor: PRIMARY,
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '15px',
                                textTransform: 'none',
                                boxShadow: '0px 4px 12px rgba(0, 180, 216, 0.2)',
                                '&:hover': {
                                    backgroundColor: PRIMARY_DARK,
                                    boxShadow: '0px 6px 16px rgba(0, 180, 216, 0.3)',
                                },
                            }}
                        >
                            {tLocal.goDashboard}
                        </Button>

                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleViewProject}
                            startIcon={<IconChecks size={20} />}
                            sx={{
                                borderRadius: '50px',
                                height: '52px',
                                color: colors.carbon_text_main || '#1C252E',
                                borderColor: colors.carbon_border_solid || '#E0E5EB',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '15px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: colors.carbon_border_selected || '#00B4D8',
                                    backgroundColor: '#FFFFFF',
                                },
                            }}
                        >
                            {tLocal.viewProject}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CarbonCreditsCongratulations;
